import React, { useState, useMemo } from 'react';
import { View, Pressable, TextInput } from 'react-native-web';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomText from '../components/atoms/CustomText';
import Button from '../components/common/Button';
import { colors, spacing } from '../utils/styling';
import { useResponsive } from '../hooks/useResponsive';
import { ONBOARDING_QUESTIONS } from '../utils/onboardingQuestions';
import { useCreateLead } from '../lib/leads';

// Advisor-sent onboarding intake (ClickUp 86badfke7). The deeper financial-asset +
// literacy questionnaire an advisor shares with a prospect. The link carries
// ?advisor=<public_id> so the resulting lead is attributed + routed to the advisor's
// firm (per-firm routing rules use the net-worth answer). Separate from the free
// teaser quiz. Lightweight contact capture up front, then the 17 questions.

export default function Onboarding() {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [searchParams] = useSearchParams();
  const advisorPublicId = searchParams.get('advisor');

  const { mutateAsync: createLead, isPending } = useCreateLead();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const setAnswer = (key: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  const allAnswered = useMemo(
    () => ONBOARDING_QUESTIONS.every((q) => !!answers[q.key]),
    [answers]
  );

  const contactValid = useMemo(() => {
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return firstName.trim() && lastName.trim() && emailRe.test(email);
  }, [firstName, lastName, email]);

  const handleSubmit = async () => {
    setError('');
    if (!contactValid) {
      setError('Please enter your name and a valid email.');
      return;
    }
    if (!allAnswered) {
      setError('Please answer every question before submitting.');
      return;
    }
    try {
      await createLead({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        advisor_public_id: advisorPublicId,
        onboarding: answers,
      });
      setSubmitted(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Something went wrong submitting your answers. Please try again.'
      );
    }
  };

  if (submitted) {
    return (
      <View style={{ flex: 1, minHeight: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.gray[100], padding: spacing.lg }}>
        <View style={{ maxWidth: 520, alignItems: 'center' }}>
          <CustomText style={{ fontFamily: 'Nunito', fontSize: isMobile ? 26 : 34, fontWeight: '600', color: colors.dark, textAlign: 'center', marginBottom: spacing.md }}>
            Thank you!
          </CustomText>
          <CustomText style={{ fontFamily: 'Nunito', fontSize: 16, color: colors.gray[600], textAlign: 'center', marginBottom: spacing.xl }}>
            Your responses have been sent to your advisor. They'll be in touch with what's next.
          </CustomText>
          <Button label="Take the Money Personality quiz" onPress={() => navigate('/quiz')} rounded={false} style={{ height: 44 }} />
        </View>
      </View>
    );
  }

  const inputStyle = {
    height: 40,
    borderWidth: 1,
    borderColor: colors.gray[200],
    borderRadius: 6,
    paddingHorizontal: 12,
    outlineStyle: 'none' as const,
    width: '100%',
    fontFamily: 'Nunito',
    fontSize: 14,
    backgroundColor: colors.white,
  };

  return (
    <View style={{ flex: 1, minHeight: '100vh', backgroundColor: colors.gray[100], alignItems: 'center', padding: spacing.lg, overflowY: 'auto' }}>
      <View style={{ width: '100%', maxWidth: 640, paddingVertical: spacing.xl }}>
        <img
          src="/transparent-logo.png"
          alt="Money Personality Logo"
          style={{ height: 56, width: 'auto', objectFit: 'contain', cursor: 'pointer', marginBottom: spacing.lg }}
          onClick={() => navigate('/')}
        />
        <CustomText style={{ fontFamily: 'Nunito', fontSize: isMobile ? 26 : 36, fontWeight: '400', color: colors.dark, marginBottom: spacing.xs }}>
          A few financial questions
        </CustomText>
        <CustomText style={{ fontFamily: 'Nunito', fontSize: 16, color: colors.gray[600], marginBottom: spacing.xl }}>
          Help your advisor understand your relationship with money — it takes less than 5 minutes.
        </CustomText>

        {/* Contact */}
        <View style={{ backgroundColor: colors.white, borderRadius: 12, padding: spacing.lg, marginBottom: spacing.lg, borderWidth: 1, borderColor: colors.gray[200] }}>
          <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: spacing.md, marginBottom: spacing.md }}>
            <View style={{ flex: 1 }}>
              <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>First name</CustomText>
              <TextInput value={firstName} onChangeText={setFirstName} placeholder="Jane" placeholderTextColor={colors.gray[500]} style={inputStyle} />
            </View>
            <View style={{ flex: 1 }}>
              <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>Last name</CustomText>
              <TextInput value={lastName} onChangeText={setLastName} placeholder="Doe" placeholderTextColor={colors.gray[500]} style={inputStyle} />
            </View>
          </View>
          <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: spacing.md }}>
            <View style={{ flex: 1 }}>
              <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>Email</CustomText>
              <TextInput value={email} onChangeText={setEmail} placeholder="jane@example.com" placeholderTextColor={colors.gray[500]} style={inputStyle} />
            </View>
            <View style={{ flex: 1 }}>
              <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>Phone (optional)</CustomText>
              <TextInput value={phone} onChangeText={setPhone} placeholder="(555) 123-4567" placeholderTextColor={colors.gray[500]} style={inputStyle} />
            </View>
          </View>
        </View>

        {/* Questions */}
        {ONBOARDING_QUESTIONS.map((q, idx) => (
          <View key={q.key} style={{ backgroundColor: colors.white, borderRadius: 12, padding: spacing.lg, marginBottom: spacing.md, borderWidth: 1, borderColor: colors.gray[200] }}>
            <CustomText style={{ fontFamily: 'Nunito', fontSize: 16, fontWeight: '600', color: colors.dark, marginBottom: spacing.md }}>
              {idx + 1}. {q.question}
            </CustomText>
            <View style={{ gap: spacing.sm }}>
              {q.options.map((opt) => {
                const selected = answers[q.key] === opt;
                return (
                  <Pressable
                    key={opt}
                    onPress={() => setAnswer(q.key, opt)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                      padding: spacing.sm,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: selected ? colors.primary : colors.gray[200],
                      backgroundColor: selected ? colors.gray[100] : colors.white,
                      cursor: 'pointer',
                    }}
                  >
                    <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: selected ? colors.primary : colors.gray[400], alignItems: 'center', justifyContent: 'center' }}>
                      {selected ? <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary }} /> : null}
                    </View>
                    <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.dark, flex: 1 }}>{opt}</CustomText>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ))}

        {error ? (
          <CustomText style={{ color: colors.danger, fontFamily: 'Nunito', fontSize: 14, marginBottom: spacing.md }}>{error}</CustomText>
        ) : null}

        <Button
          label="Submit my answers"
          onPress={handleSubmit}
          loading={isPending}
          disabled={isPending}
          rounded={false}
          style={{ width: '100%', height: 44, marginBottom: spacing.xxl }}
        />
      </View>
    </View>
  );
}
